import {Draggable, gsap, InertiaPlugin, Flip} from "gsap/all";
import $ from "jquery";
import {DotLottie} from "@lottiefiles/dotlottie-web"

// Set the WASM URL
DotLottie.setWasmUrl("js/libs/dotlottie-player.wasm");

export class Equipment {

  constructor(target, data) {
    this.$widget = target;

    this.currentBuildingType = null;
    this.currentMode = null;

    this.init();
  }

  init() {
    // Limit the amount of visible equipment in the listing
    // Loop per equipment container (per building)
    const maxTotal = 10;
    this.$widget.find('.equipment-container').each((i, container) => {
      const $container = $(container);
      const $elements = $container.find('.equipment-element');

      // Group elements by prefix
      const groups = {RTU: [], AHU: [], CHILLER: []};
      $elements.each((j, el) => {
        const eq = $(el).attr('data-equipment');
        if (!eq) return;
        const prefix = Object.keys(groups).find(p => eq.startsWith(p + '_'));
        if (prefix) groups[prefix].push($(el));
      });

      // Keep only non-empty groups
      const activeGroups = Object.values(groups).filter(g => g.length > 0);
      if (!activeGroups.length) return;
      const totalItems = activeGroups.reduce((sum, g) => sum + g.length, 0);

      // If total <= 10 → show all
      if (totalItems <= maxTotal) return;

      // 2 or 3 groups → max 10 total, try to balance but always fill to 10 if possible
      $elements.hide();

      // Start with equal base allocation
      const base = Math.floor(maxTotal / activeGroups.length);
      const allocations = activeGroups.map(g => Math.min(g.length, base));
      let allocated = allocations.reduce((sum, n) => sum + n, 0);

      // Fill remaining slots until we reach 10
      let iGroup = 0;
      while (allocated < maxTotal) {
        if (allocations[iGroup] < activeGroups[iGroup].length) {
          allocations[iGroup]++;
          allocated++;
        }
        iGroup = (iGroup + 1) % activeGroups.length;
      }

      // Apply visibility (respect order inside each group)
      activeGroups.forEach((group, i) => group.forEach(($el, j) => $el.toggle(j < allocations[i])));
    });
  }

  changeEquipements(buildingType, mode) {
    const buildingChanged = (this.currentBuildingType !== buildingType);

    // Debounce + cancel any in-flight tween so rapid slider scrubbing
    // doesn't queue up animations behind a moving target.
    if (this.equipmentDelay) this.equipmentDelay.kill();
    if (this.equipmentTween) this.equipmentTween.kill();

    const applyChange = () => {
      const $oldBuilding = (this.currentBuildingType !== null) ? this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"]`) : $();

      this.currentBuildingType = buildingType;
      this.currentMode = mode;

      const swapIn = () => {
        this.resetStates();

        // Set mode to equipment listing, so it can show heating/cooling
        this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"] .equipment-element`).attr("data-mode", mode);

        const $newBuilding = this.$widget.find(`.equipment-container[data-building="${buildingType}"]`);
        $newBuilding.show();
        this.$widget.find(`.equipment-container[data-building="${buildingType}"] .equipment-images[data-mode="${mode}"]`).show();

        if (buildingChanged) {
          this.equipmentTween = gsap.fromTo($newBuilding, {opacity: 0}, {opacity: 1, duration: 0.3, ease: "power1.out"});
        } else {
          // Do not animate if building didn't change to avoid flickering
          gsap.set($newBuilding, {opacity: 1});
        }
      };

      if (buildingChanged && $oldBuilding.length) {
        // Crossfade old building with new one if it changed
        this.equipmentTween = gsap.to($oldBuilding, {opacity: 0, duration: 0.2, ease: "power1.in", onComplete: swapIn});
      } else {
        // If no old building, just swap in the new one
        swapIn();
      }
    };

    if (buildingChanged) {
      // Add a small delay (debounce) to avoid flickering when switching buildings quickly
      this.equipmentDelay = gsap.delayedCall(0.15, applyChange);
    } else {
      applyChange();
    }
  }

  updateEquipmentsState(id, state, chaosMode = false) {
    if (this.currentBuildingType !== null && this.currentMode !== null) {

      // Compass Chaos mode exception
      const targetMode = (chaosMode === true) ? state : this.currentMode;
      if (chaosMode === true && state !== "active") this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"] .equipment-element[data-equipment="${id}"]`).attr("data-mode", targetMode);
      else if (chaosMode === true && state === "active") this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"] .equipment-element[data-equipment="${id}"]`).removeClass("active").attr("data-mode", this.currentMode).removeClass("active");

      // this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"] .equipment-element`).removeClass("active");
      this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"] .equipment-element[data-equipment="${id}"][data-mode="${targetMode}"]`).toggleClass("active", state);

      // Update image, keep active until all equipment is off
      const hasActiveEquipement = this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"] .equipment-element[data-mode="${this.currentMode}"].active`).length > 0;
      const $equipmentEl = this.$widget.find(`.equipment-container[data-building="${this.currentBuildingType}"] .equipment-images[data-mode="${this.currentMode}"] [data-equipment]`)
      $equipmentEl.toggle(hasActiveEquipement);

      // Send equipment state to screen bridge
      const humanReadableState = (state !== false) ? targetMode : "active";
      app.sendScreenBridgeMessage(`updateEquipmentsState ${id} ${humanReadableState}`);

      if ($equipmentEl.hasClass("lottie")) {
        const currentLottie = $equipmentEl.data("lottie");
        if (currentLottie === undefined) {
          // console.log("init lottie", $equipmentEl, $equipmentEl.data("path"))
          $equipmentEl.attr("id", `equipement-lottie-${Math.random().toString(36).slice(2, 10)}`);

          const lottie = new DotLottie({
            canvas: $equipmentEl.get(0),
            loop: true,
            autoplay: true,
            src: $equipmentEl.data("path"),
            renderConfig: {devicePixelRatio: 2},
          });
          $equipmentEl.data("lottie", lottie);
        } else {
          // console.log("use current lottie")
          const lottie = $equipmentEl.data("lottie");
          lottie.play();
        }
      }

      if ($equipmentEl.length > 0 && $equipmentEl.data("is-playing") !== hasActiveEquipement) gsap.fromTo($equipmentEl, {opacity: 0}, {opacity: 1})
      $equipmentEl.data("is-playing", hasActiveEquipement)
    }
  }

  resetStates() {
    this.$widget.find('.equipment-container').hide();
    this.$widget.find('.equipment-images').hide();
    this.$widget.find(`.equipment-container .equipment-element`).removeClass("active");
    this.$widget.find(`.equipment-container .equipment-images img[data-equipment]`).hide();

    this.$widget.find(`.equipment-container .equipment-images canvas[data-equipment]`).each((i, canvas) => {
      $(canvas).hide();
      const lottie = $(canvas).data("lottie");
      if (lottie) {
        lottie.stop();
      }
    });

    // Send equipment state (off) to screen bridge
    this.$widget.find(`.equipment-container[data-building=${this.currentBuildingType}] .equipment-element`).each((index, elem) => {
      const id = $(elem).data("equipment");
      app.sendScreenBridgeMessage(`updateEquipmentsState ${id} active`);
    })
  }

  reset() {
    this.resetStates();
    this.destroyLotties();

    this.currentBuildingType = null;
    this.currentMode = null;
  }

  // Make sure to destroy lotties on reset to free up memory
  destroyLotties() {
    this.$widget.find(`.equipment-container .equipment-images canvas[data-equipment]`).each((i, canvas) => {
      const lottie = $(canvas).data("lottie");
      if (lottie) {
        lottie.destroy();
        $(canvas).removeData("lottie");
      }
    });
  }
}
